import type { ConnectionResponse } from '~/features/onboarding/types/connection.types';

import type {
  ResolvedTransformer,
  TransformerConnector,
  TransformerDefinition,
} from '../types/transformer.types';

/** Writer format → human label (prototype copy). Unknown formats show upper-cased. */
const FORMAT_LABELS: Record<string, string> = {
  csv: 'CSV',
  tsv: 'TSV',
  txt: 'TXT (pipe)',
  json: 'JSON',
  jsonl: 'JSON Lines',
  xml: 'XML',
  xlsx: 'Excel (.xlsx)',
};

/** Display label for a writer format code. */
export function formatLabel(format: string | null | undefined): string {
  if (!format) return '—';
  return FORMAT_LABELS[format.toLowerCase()] ?? format.toUpperCase();
}

/** Every writer format the library filter offers, in prototype order. */
export const FORMAT_OPTIONS = Object.keys(FORMAT_LABELS);

/** "Meta" rather than the wire code `META`; GMC stays an acronym. */
export function connectorLabel(connector: string): string {
  return connector === 'META' ? 'Meta' : connector;
}

/**
 * True when resolution fell through to the standard export — nothing is assigned,
 * so the export sheet keeps its existing template / columns path.
 */
export function isStandardResolution(item: ResolvedTransformer | undefined): boolean {
  return !item || item.reason === 'standard';
}

/**
 * True when a connection delivers files over SFTP: an SFTP connection, or a Google
 * Merchant / Meta Commerce connection whose delivery method is SFTP (and delivery
 * isn't switched off). Transformer assignments and file layouts apply to all of them.
 */
export function deliversOverSftp(connection: ConnectionResponse): boolean {
  if (connection.type === 'SFTP') return true;
  if (connection.type !== 'GOOGLE_MERCHANT' && connection.type !== 'META_COMMERCE') return false;
  const { config } = connection;
  return config.delivery_method === 'SFTP' && config.delivery_enabled !== false;
}

/** The column set a connection's deliveries carry: Meta for Meta Commerce, else GMC (§8). */
export function connectionConnector(connection: ConnectionResponse): TransformerConnector {
  return connection.type === 'META_COMMERCE' ? 'META' : 'GMC';
}

/**
 * i18n key + params for the hint beside an SFTP-delivering connection's name, e.g.
 * "SFTP server" or "Google Merchant · SFTP delivery", so look-alike names stay apart.
 */
export function sftpConnectionHint(connection: ConnectionResponse): {
  key: 'scope.sftpServer' | 'scope.sftpDelivery';
  connector?: string;
} {
  if (connection.type === 'SFTP') return { key: 'scope.sftpServer' };
  return {
    key: 'scope.sftpDelivery',
    connector: connection.type === 'META_COMMERCE' ? 'Meta Commerce' : 'Google Merchant',
  };
}

/** The empty definition a "Start blank" transformer begins from: every source column, TSV. */
export function blankDefinition(connector: TransformerConnector): TransformerDefinition {
  return {
    connector,
    source: 'products',
    content: [],
    writer: { format: 'tsv', options: { header: true, quoting: 'none', lineEnding: 'LF' } },
    file: [],
  };
}

/** Comparable snapshot of a definition, for dirty checks. */
export function definitionLines(definition: TransformerDefinition | undefined): string[] {
  if (!definition) return [];
  return JSON.stringify(
    {
      content: definition.content,
      writer: definition.writer,
      file: definition.file,
    },
    null,
    2,
  ).split('\n');
}

/** Stable stringification for equality checks. */
export function sameDefinition(
  a: TransformerDefinition | undefined,
  b: TransformerDefinition | undefined,
): boolean {
  return definitionLines(a).join('\n') === definitionLines(b).join('\n');
}

/** Renders one cell for the preview table: lists and objects as compact JSON. */
export function cellText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

/** "a, b  c" → ["a", "b", "c"] — pinned product ids from the sample bar. */
export function parsePinnedIds(raw: string): string[] {
  return [...new Set(raw.split(/[\s,]+/).filter(Boolean))];
}
